import React from 'react';
import { useTheme } from '@mui/material/styles';
import { TextField, InputAdornment, Box, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GlassmorphismDesignSystem from '../theme/GlassmorphismDesignSystem';
import { useAutoResize } from '../utils/autoResizeUtils';
import { darkSurfaces } from '../theme/darkSurfaces';



const Input = ({
  name,
  label,
  value,
  onChange,
  error,
  type = 'text',
  variant = 'outlined',
  fullWidth = true,
  required = false,
  help,
  multiline,
  autoResize = true,
  minHeight = 42,
  maxHeight = 200,
  keyStructure,
  handleKeyPress,
  fieldKey,
  fieldType,
  validation,
  transform,
  allowEmpty = false,
  ...props
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = React.useState(false);
  const isNumber = type === 'number';
  const [clearing, setClearing] = React.useState(false);

  React.useEffect(() => {
    if (isNumber) setClearing(false);
  }, [value, isNumber]);

  const handleNumberChange = (e) => {
    if (e.target.value === '') {
      setClearing(true);
      // Par défaut un champ vidé garde sa dernière valeur ; `allowEmpty` transmet le vide (ex. quantité vidée = 0).
      if (allowEmpty && onChange) onChange(e);
    } else {
      setClearing(false);
      if (onChange) onChange(e);
    }
  };

  // Auto-resize functionality for multiline inputs
  const { ref: autoResizeRef } = useAutoResize({
    minHeight: multiline ? minHeight : undefined,
    maxHeight: multiline ? maxHeight : undefined,
    lineHeight: 20,
  });

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isPassword = type === 'password';

  const textFieldSx = {
    background: isDarkMode ? 'transparent' : GlassmorphismDesignSystem.patterns.interactive.background(theme),
    borderRadius: '8px',
    backdropFilter: isDarkMode ? 'none' : GlassmorphismDesignSystem.patterns.interactive.backdropFilter,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      ...(isDarkMode && { backgroundColor: darkSurfaces.inset, backgroundImage: 'none' }),
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 12px',
      border: 'none',
      resize: multiline ? 'none' : 'initial',
      minHeight: multiline && autoResize ? `${minHeight - 24}px` : 'auto',
      '&::placeholder': {
        fontSize: '11px',
      },
    },
    '& .MuiInputAdornment-root': {
      marginRight: '8px',
      '& .MuiSvgIcon-root': {
        fontSize: '1.25rem',
      },
    },
  };

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          component="label"
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: 'text.secondary',
            mb: '2px',
            display: 'block',
          }}
        >
          {label}
          {required && ' *'}
        </Typography>
      )}
      <TextField
        sx={textFieldSx}
        name={name}
        placeholder={props.placeholder || t('typeHere')}
        value={isNumber ? (clearing ? '' : (value ?? '')) : value}
        onChange={isNumber ? handleNumberChange : onChange}
        type={isPassword ? (showPassword ? 'text' : 'password') : type}
        variant={variant}
        fullWidth={fullWidth}
        required={required}
        multiline={multiline}
        rows={multiline && !autoResize ? 3 : undefined}
        error={Boolean(error)}
        helperText={(typeof error === 'string' && error) ? error : help}
        size="small"
        inputRef={multiline && autoResize ? autoResizeRef : undefined}
        InputProps={{
          endAdornment: isPassword ? (
            <InputAdornment
              position="end"
              onClick={handlePasswordVisibility}
              style={{ cursor: 'pointer' }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </InputAdornment>
          ) : (
            props.endAdornment
          ),
          ...props.InputProps,
        }}
        {...props}
      />
    </Box>
  );
};

export default Input;
